import Page from '../../components/templates/Page'
import Welcome from '../../components/pages/singleBlog/Welcome'
import Container from '../../components/elements/Container'
import IconSummary from '/public/icons/icon-summary.svg'
import IconContents from '/public/icons/icon-table-contents.svg'
import { getS3Url } from '../../components/helpers/getS3Url'
import LazyImage from '../../components/elements/LazyImage'
import axios from '/components/configs/axios'
import styles from './styles.module.scss'
import Paragraph from '../../components/pages/singleBlog/Paragraph'
import Title from '../../components/pages/singleBlog/Title'
import SideContent from '../../components/pages/singleBlog/SideContent'
import ThreeImages from '../../components/pages/singleBlog/ThreeImages'
import Subscribe from '../../components/pages/blog/Subscribe'
import Actions from '../../components/pages/singleBlog/Actions'
import isEmpty from 'lodash-es/isEmpty'
import Columns from '../../components/layout/Columns'
import Column from '../../components/layout/Column'
import BlogCard from '../../components/custom/BlogCard'
import Section from '../../components/elements/Section'
import { useResponsive } from '../../providers/ResponsiveContext'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import FourOhFour from '../404'
import LoadingPage from '../../components/templates/LoadingPage'
import headerTranslation from '/components/general/Header/headerTranslation'
import footerTranslation from '/components/general/Footer/footerTranslations'
import singleBlogTranslations from '../../components/pages/singleBlog/singleBlogTranslations'
import cache from 'memory-cache'
import dynamic from 'next/dynamic'
import moment from 'moment'

const ReactPlayer = dynamic(() => import('react-player'), {
  ssr: false,
  loading: () => <div>Loading...</div>
})

const ScrollLink = dynamic(
  () => import('react-scroll').then((mod) => mod.Link),
  { ssr: false }
)

const CloudflareVideo = dynamic(
  () => import('../../components/elements/CloudflareVideo'),
  {
    ssr: false
  }
)
const SingleBlog = ({
  blog,
  headerTranslation,
  footerTranslation,
  pageTranslation,
  notFound
}) => {
  const router = useRouter()
  const { isMobile, isDesktop } = useResponsive()
  const [headings, setHeadings] = useState('')
  const [hasWindow, setHasWindow] = useState(false)

  const loadingTitle = {
    en: 'Loading',
    fr: 'Chargement',
    es: 'Cargando',
    nl: 'Bezig met laden'
  }

  if (router.isFallback) {
    return <LoadingPage title={loadingTitle[router.locale]} />
  }

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const elements = Array.from(document.querySelectorAll('h3, h4'))

      setHeadings(elements)

      elements.map((element, index) => {
        element.setAttribute('id', `heading-${index}`)
      })
    }
  }, [])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setHasWindow(true)
    }
  }, [])

  if (notFound || !blog?.article) {
    return (
      <FourOhFour
        headerTranslation={headerTranslation}
        footerTranslation={footerTranslation}
      />
    )
  }

  return (
    <Page
      headerTranslation={headerTranslation}
      footerTranslation={footerTranslation}
      footer={false}
      meta={{
        title: `${blog?.title} | St. Martin Sotheby's Realty`,
        description: blog.short_description
          ?.replaceAll('<p>', '')
          ?.replaceAll('</p>', ''),
        image: getS3Url(blog.cloudflare_hero_image || blog.hero_image, '1980')
      }}
    >
      <Welcome
        title={blog?.title}
        date={moment(blog.published_date).utc().format('MMM DD, YYYY')}
        image={blog.cloudflare_hero_image || blog.hero_image}
      />

      <div className={styles.page}>
        <Container>
          <div className={styles.blog}>
            <Actions blog={blog} />

            <div className={styles.blog__content}>
              {blog.summary_content && (
                <Container variant={['-xs2', '-pxl']}>
                  <div className={styles.blog__welcome}>
                    <div className={styles.blog__title}>
                      <IconSummary />

                      <h2>{pageTranslation.content.summary[router.locale]}</h2>
                    </div>

                    <div
                      dangerouslySetInnerHTML={{
                        __html: blog.summary_content
                      }}
                    ></div>

                    {headings?.length !== 0 && (
                      <div className={styles.blog__table}>
                        <div className={styles.blog__title}>
                          <IconContents />

                          <h2>{pageTranslation.content.toc[router.locale]}</h2>
                        </div>

                        <ul>
                          {headings.map((item, index) => (
                            <li
                              key={`nav-link-${index}`}
                              className={styles.blog__link}
                            >
                              {hasWindow && (
                                <ScrollLink
                                  spy={true}
                                  smooth={true}
                                  offset={-200}
                                  duration={500}
                                  to={`heading-${index}`}
                                >
                                  {item.innerText}
                                </ScrollLink>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </Container>
              )}

              {blog.article &&
                blog.article.map((item, index) => {
                  switch (item.type) {
                    case 'paragraph':
                      return (
                        <Paragraph
                          key={`inner-section-${index}`}
                          content={item.data.article_content_paragraph}
                        />
                      )

                    case 'title':
                      return (
                        <Title
                          key={`inner-section-${index}`}
                          count={index}
                          content={item.data.article_content_title}
                        />
                      )

                    case 'paragraph_and_side_images':
                      return (
                        <SideContent
                          key={`inner-section-${index}`}
                          content={item}
                        />
                      )

                    case 'full_width_image':
                      if (
                        item.data.full_width_image ||
                        item.data.cloudflare_full_width_image
                      ) {
                        return (
                          <div
                            key={`inner-section-${index}`}
                            className={styles.blog__section}
                          >
                            <Container variant={['-xs', isDesktop && '-p0']}>
                              <div className={styles.blog__image}>
                                <LazyImage
                                  sizes="(min-width: 1250px) 978px, (max-width: 1250px) 100vw, 70vw"
                                  objectFit="cover"
                                  objectPosition="center"
                                  layout="fill"
                                  src={getS3Url(
                                    item.data.cloudflare_full_width_image ||
                                      item.data.full_width_image
                                  )}
                                />
                              </div>
                            </Container>
                          </div>
                        )
                      }
                      return
                    case 'three_images':
                      return (
                        <ThreeImages
                          key={`inner-section-${index}`}
                          content={item}
                        />
                      )
                    case 'video':
                      if (hasWindow) {
                        return (
                          <Container
                            key={`-video-${index}`}
                            variant={[isMobile && '-p0']}
                          >
                            <div className={styles.blog__section}>
                              {(item.data.own_video ||
                                item.data.cloudflare_own_video) && (
                                <div>
                                  {item.data.cloudflare_own_video ? (
                                    <CloudflareVideo
                                      loop
                                      src={item.data.cloudflare_own_video}
                                      height="auto"
                                      controls
                                    />
                                  ) : (
                                    <ReactPlayer
                                      loop
                                      url={getS3Url(item.data.own_video)}
                                      controls
                                      width="100%"
                                      height="auto"
                                    />
                                  )}
                                </div>
                              )}

                              <div
                                className={
                                  item.data.youtube_video && styles.blog__video
                                }
                              >
                                {item.data.youtube_video && (
                                  <ReactPlayer
                                    loop
                                    url={
                                      item.data.youtube_video ||
                                      getS3Url(item.data.own_video)
                                    }
                                    controls
                                    width="100%"
                                    height="auto"
                                  />
                                )}
                              </div>
                            </div>
                          </Container>
                        )
                      }
                      return
                  }
                })}

              {blog.footnote && (
                <Container variant={['-xs2', '-pxl']}>
                  <div
                    dangerouslySetInnerHTML={{ __html: blog.footnote }}
                    className={styles.blog__footnote}
                  ></div>
                </Container>
              )}
            </div>

            <Subscribe
              title={pageTranslation.subscribe.title[router.locale]}
              description={pageTranslation.subscribe.description[router.locale]}
              translations={footerTranslation.form}
              variant={['gray', 'separator', 'separator-top']}
            />

            {!isEmpty(blog.recommended) && (
              <Section separator variant={['white', 'separator-top', 'p-xl']}>
                <Container variant={['-xs']}>
                  <div className={styles.blog__intro}>
                    {pageTranslation.cards.explore[router.locale]}
                  </div>

                  <h2 className={styles.blog__recommended}>
                    {blog.categories}
                  </h2>

                  <Columns className={styles.columns}>
                    <Column spans={isMobile ? 12 : isDesktop ? 6 : 7}>
                      <BlogCard
                        {...blog.recommended[0]}
                        variant={['similar']}
                        image={
                          blog.recommended[0].picture ||
                          blog.recommended[0].image
                        }
                        buttonLabel={
                          pageTranslation.cards.button[router.locale]
                        }
                      />
                    </Column>

                    {blog.recommended[1] && (
                      <Column spans={isMobile ? 12 : isDesktop ? 6 : 5}>
                        <BlogCard
                          {...blog.recommended[1]}
                          variant={['similar']}
                          image={
                            blog.recommended[1].picture ||
                            blog.recommended[1].picture
                          }
                          buttonLabel={
                            pageTranslation.cards.button[router.locale]
                          }
                        />
                      </Column>
                    )}
                  </Columns>
                </Container>
              </Section>
            )}
          </div>
        </Container>
      </div>
    </Page>
  )
}

export async function getStaticPaths({ locales }) {
  const data = await axios
    .get('blog/post-name')
    .then((res) => res.data)
    .catch((error) => console.error(error))

  const paths =
    data &&
    data
      .map((post) =>
        locales.map((locale) => ({
          params: { _name: post },
          locale
        }))
      )
      .flat()

  return {
    paths,
    fallback: true
  }
}

export async function getStaticProps({ params, locale }) {
  const { _name } = params

  const data = await axios
    .get(`blog/post/${_name}`)
    .then((res) => res.data)
    .catch((error) => console.error(error))

  let headerDataTranslation = cache.get('headerTranslations')
  if (!headerDataTranslation) {
    headerDataTranslation = await headerTranslation()
    cache.put('headerTranslations', headerDataTranslation)
  }

  // caching for footer translations
  let footerDataTranslation = cache.get('footerTranslations')
  if (!footerDataTranslation) {
    footerDataTranslation = await footerTranslation()
    cache.put('footerTranslations', footerDataTranslation)
  }

  let blogTranslation = await singleBlogTranslations()

  return {
    props: {
      blog: data?.[locale],
      notFound: !data || data.length === 0,
      headerTranslation: headerDataTranslation,
      footerTranslation: footerDataTranslation,
      pageTranslation: blogTranslation
    },
    revalidate: 300
  }
}
export default SingleBlog
